import React from 'react';
import { motion } from 'framer-motion';
import { useModal } from '../services/ModalService';
import '../styles/pages/DevOpsSRE.css';

const DevOpsSRE = () => {
  const { openContactModal } = useModal();

  return (
    <div className="devops-page">
      {/* Hero Section */}
      <section className="devops-hero">
        <div className="devops-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ textAlign: "center" }}
          >
            <div className="hero-badge">
              ⚙️ DevOps Excellence
            </div>
            
            <h1 className="hero-title"
            dangerouslySetInnerHTML={{ __html: "Advanced <span class=\"gradient-text\">DevOps & SRE</span><br />Solutions" }}
            />
            
            <p className="hero-subtitle">
              Streamline your software delivery with cutting-edge DevOps practices and Site Reliability Engineering. Accelerate deployment cycles while maintaining system reliability and performance.
            </p>
            
            <div className="hero-buttons">
              <button 
                className="btn-primary"
                onClick={() => openContactModal(
                  'DevOps Services',
                  'DevOps SRE Page - Optimize Pipeline'
                )}
              >
                Optimize Pipeline
              </button>
              <button 
                className="btn-secondary"
                onClick={() => openContactModal(
                  'DevOps Assessment Request',
                  'DevOps SRE Page - DevOps Assessment'
                )}
              >
                DevOps Assessment
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="devops-section">
        <div className="devops-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="devops-section-title gradient-text">DevOps & SRE Services</h2>
            <p className="devops-section-subtitle">
              Comprehensive DevOps solutions to accelerate software delivery and ensure reliable, scalable systems
            </p>
            
            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))", 
              gap: "30px", 
              marginTop: "60px" 
            }}>
              
              <div className="devops-glass-card">
                <h3 style={{ color: "#8B5CF6", marginBottom: "20px", fontSize: "1.5rem" }}>CI/CD Pipeline Implementation</h3>
                <p style={{ marginBottom: "20px", lineHeight: "1.6" }}>Automated continuous integration and deployment pipelines for faster, more reliable software delivery.</p>
                <ul style={{ listStyle: "none", padding: 0, color: "#666" }}>
                  <li style={{ marginBottom: "8px" }}>✓ Automated Testing Integration</li>
                  <li style={{ marginBottom: "8px" }}>✓ Deployment Automation</li>
                  <li style={{ marginBottom: "8px" }}>✓ Code Quality Gates</li>
                  <li style={{ marginBottom: "8px" }}>✓ Release Management</li>
                </ul>
              </div>
              
              <div className="devops-glass-card">
                <h3 style={{ color: "#8B5CF6", marginBottom: "20px", fontSize: "1.5rem" }}>Infrastructure as Code</h3>
                <p style={{ marginBottom: "20px", lineHeight: "1.6" }}>Manage and provision infrastructure through code for consistency, scalability, and version control.</p>
                <ul style={{ listStyle: "none", padding: 0, color: "#666" }}>
                  <li style={{ marginBottom: "8px" }}>✓ Terraform & CloudFormation</li>
                  <li style={{ marginBottom: "8px" }}>✓ Configuration Management</li>
                  <li style={{ marginBottom: "8px" }}>✓ Environment Provisioning</li>
                  <li style={{ marginBottom: "8px" }}>✓ Infrastructure Versioning</li>
                </ul>
              </div>
              
              <div className="devops-glass-card">
                <h3 style={{ color: "#8B5CF6", marginBottom: "20px", fontSize: "1.5rem" }}>Monitoring & Observability</h3>
                <p style={{ marginBottom: "20px", lineHeight: "1.6" }}>Comprehensive monitoring solutions to ensure system reliability and performance optimization.</p>
                <ul style={{ listStyle: "none", padding: 0, color: "#666" }}>
                  <li style={{ marginBottom: "8px" }}>✓ Application Performance Monitoring</li>
                  <li style={{ marginBottom: "8px" }}>✓ Infrastructure Monitoring</li>
                  <li style={{ marginBottom: "8px" }}>✓ Log Aggregation & Analysis</li>
                  <li style={{ marginBottom: "8px" }}>✓ Alerting & Incident Response</li>
                </ul>
              </div>
              
              <div className="devops-glass-card">
                <h3 style={{ color: "#8B5CF6", marginBottom: "20px", fontSize: "1.5rem" }}>Site Reliability Engineering</h3>
                <p style={{ marginBottom: "20px", lineHeight: "1.6" }}>Ensure system reliability, availability, and performance through proactive engineering practices.</p>
                <ul style={{ listStyle: "none", padding: 0, color: "#666" }}>
                  <li style={{ marginBottom: "8px" }}>✓ SLA/SLO Management</li>
                  <li style={{ marginBottom: "8px" }}>✓ Incident Management</li>
                  <li style={{ marginBottom: "8px" }}>✓ Capacity Planning</li>
                  <li style={{ marginBottom: "8px" }}>✓ Disaster Recovery</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="devops-section">
        <div className="devops-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h2 className="devops-section-title gradient-text">DevOps Benefits</h2>
            <p className="devops-section-subtitle">
              Transform your development and operations with proven DevOps practices
            </p>
            
            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", 
              gap: "30px",
              marginTop: "60px"
            }}>
              
              <div className="devops-glass-card" style={{ textAlign: "center" }}>
                <div style={{ fontSize: "2.5rem", marginBottom: "15px" }}>🚀</div>
                <h4 style={{ color: "#8B5CF6", marginBottom: "10px", fontSize: "1.2rem" }}>Faster Deployment</h4>
                <p style={{ color: "#666", lineHeight: "1.5" }}>Reduce deployment time from days to minutes with automated pipelines</p>
              </div>
              
              <div className="devops-glass-card" style={{ textAlign: "center" }}>
                <div style={{ fontSize: "2.5rem", marginBottom: "15px" }}>🔄</div>
                <h4 style={{ color: "#8B5CF6", marginBottom: "10px", fontSize: "1.2rem" }}>Improved Reliability</h4>
                <p style={{ color: "#666", lineHeight: "1.5" }}>Enhance system stability and reduce downtime with SRE practices</p>
              </div>
              
              <div className="devops-glass-card" style={{ textAlign: "center" }}>
                <div style={{ fontSize: "2.5rem", marginBottom: "15px" }}>📊</div>
                <h4 style={{ color: "#8B5CF6", marginBottom: "10px", fontSize: "1.2rem" }}>Enhanced Visibility</h4>
                <p style={{ color: "#666", lineHeight: "1.5" }}>Gain deep insights into system performance and user experience</p>
              </div>
              
              <div className="devops-glass-card" style={{ textAlign: "center" }}>
                <div style={{ fontSize: "2.5rem", marginBottom: "15px" }}>⚡</div>
                <h4 style={{ color: "#8B5CF6", marginBottom: "10px", fontSize: "1.2rem" }}>Increased Agility</h4>
                <p style={{ color: "#666", lineHeight: "1.5" }}>Respond faster to market changes with agile development practices</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="devops-section">
        <div className="devops-container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div className="devops-cta-card">
              <h3 style={{ 
                fontSize: "2rem", 
                marginBottom: "20px", 
                color: "#8B5CF6" 
              }}>
                Ready to Transform Your DevOps?
              </h3>
              <p style={{ 
                fontSize: "1.1rem", 
                marginBottom: "30px", 
                color: "#666",
                maxWidth: "600px",
                margin: "0 auto 30px"
              }}>
                Accelerate your software delivery and improve system reliability with our expert DevOps and SRE services. Let's build a more efficient future together.
              </p>
              <div style={{ display: "flex", gap: "20px", justifyContent: "center", flexWrap: "wrap" }}>
                <button 
                  className="btn-primary"
                  onClick={() => openContactModal(
                    'DevOps Transformation Project',
                    'DevOps SRE Page - Start Transformation'
                  )}
                >
                  Start Transformation
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => openContactModal(
                    'DevOps Consultation',
                    'DevOps SRE Page - DevOps Consultation'
                  )}
                >
                  DevOps Consultation
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default DevOpsSRE;